import type { CSSProperties } from 'react'

/**
 * lib/stagger.ts — list entrance orchestration for a list of ANY length.
 *
 * ── THE PROBLEM THIS REPLACES ─────────────────────────────────────────────
 * `.reveal` shipped exactly three hardcoded delay steps
 * (`index.css`: `[data-reveal-delay="1|2|3"]` → 80/160/240ms) and
 * `.detail-rise` needs a hand-written inline `--d` per element. Every org
 * surface — members, invites, devices, doc slots, seats — is a list, and a
 * list of nine items had no orchestration at all past item three. The fix is
 * NOT twelve more CSS rules: the delay is one custom property
 * (`--stagger-delay`) computed here, so one class serves a list of any length.
 *
 * ── WHY THE MATH IS IN JS AND NOT `calc()` ────────────────────────────────
 * Because the interesting part is the CAP, and a cap is where taste lives. A
 * 40-row members table with a flat 55ms step would take 2.2 seconds to finish
 * arriving, which is not "premium", it is "slow". Past `cap` every remaining
 * item shares the last delay, so a long list lands as a wave followed by a
 * block instead of a crawl. Keeping it in a pure function also makes it
 * unit-testable, which a `calc()` chain is not.
 *
 * ── REDUCED MOTION ────────────────────────────────────────────────────────
 * Handled in CSS, not here: `.stagger-rise` has an `animation: none` block
 * under `prefers-reduced-motion: reduce` in the same commit as its
 * `@keyframes` (the house convention). Callers therefore never branch, and a
 * reduced-motion user sees the list instantly and fully — never a blank list
 * waiting on an animation that will not run.
 *
 * Usage — mount entrance:
 *   {rows.map((r, i) => <li key={r.id} {...staggerProps(i)}>…</li>)}
 *
 * Usage — scroll reveal (drives the existing `.reveal` observer, uncapped):
 *   <div ref={useReveal()} className="reveal" style={staggerStyle(i)} />
 */

export interface StaggerOptions {
  /** Milliseconds between consecutive items. Default 55. */
  step?: number
  /** Milliseconds before the first item. Default 0. */
  base?: number
  /**
   * Index past which every item shares the same delay. Default 12
   * (≈660ms at the default step) — the point where a stagger stops reading
   * as choreography and starts reading as lag.
   */
  cap?: number
}

export const STAGGER_DEFAULTS: Required<StaggerOptions> = { step: 55, base: 0, cap: 12 }

/**
 * The delay for item `index`, in milliseconds. Pure — this is the unit under
 * test. Negative / non-finite indices clamp to the base delay rather than
 * producing a negative `animation-delay` (which browsers interpret as
 * "start mid-animation", i.e. a visible jump).
 */
export function staggerDelayMs(index: number, opts: StaggerOptions = {}): number {
  const { step, base, cap } = { ...STAGGER_DEFAULTS, ...opts }
  if (!Number.isFinite(index) || index <= 0) return Math.max(0, base)
  const i = Math.min(Math.floor(index), Math.max(0, cap))
  return Math.max(0, base + i * step)
}

/**
 * Inline style carrying the delay. Consumed by `.stagger-rise` and by
 * `.reveal` (which now reads the same variable, so scroll-revealed lists are
 * no longer capped at three either).
 */
export function staggerStyle(index: number, opts: StaggerOptions = {}): CSSProperties {
  return { '--stagger-delay': `${staggerDelayMs(index, opts)}ms` } as CSSProperties
}

/**
 * Everything an item needs to join the wave. Spread it:
 *   <li {...staggerProps(i)}>
 * Pass `className` to keep your own classes — they are appended, not replaced.
 */
export function staggerProps(
  index: number,
  opts: StaggerOptions & { className?: string } = {},
): { className: string; style: CSSProperties } {
  const { className, ...rest } = opts
  return {
    className: className ? `stagger-rise ${className}` : 'stagger-rise',
    style: staggerStyle(index, rest),
  }
}

/**
 * Same wave, for the scroll-reveal observer (`useReveal()` + `.reveal`).
 * Kept separate because `.reveal` starts hidden until it intersects, while
 * `.stagger-rise` runs on mount.
 */
export function revealProps(
  index: number,
  opts: StaggerOptions & { className?: string } = {},
): { className: string; style: CSSProperties } {
  const { className, ...rest } = opts
  return {
    className: className ? `reveal ${className}` : 'reveal',
    style: staggerStyle(index, rest),
  }
}
